import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";

import { messages } from "./technicalDetails.messages";
import styles from "./technicalDetails.module.scss";

import * as appConfig from "appConfig";
import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";
import { SignalFlowModal } from "./components/SignalFlowModal";
import { SpeakerDistributionSlide } from "./components/SpeakerDistributionSlide";
import { PhotoView } from "../../../photo-view/PhotoView";
import { useUi } from "@jelly/lesson";

const ACCORDION_IDS = {
  loudspeakerDistribution: "loudspeaker-distribution",
  signalFlow: "signal-flow",
} as const;

type CaseSlide = "loudspeaker-distribution" | "signal-flow";

interface Case1ContextData {
  setActiveSlide: (slide: CaseSlide) => void;
  activeSlide: CaseSlide;
}

const Case1Context = React.createContext<Case1ContextData>({
  activeSlide: "loudspeaker-distribution",
  setActiveSlide: () => {
    throw new Error("Not implemented!");
  },
});

interface TechnicalDetails {
  title: string;
}

export const TechnicalDetails: React.FC<TechnicalDetails> = ({ title }) => {
  const [activeSlide, setActiveSlide] = useState<CaseSlide>("loudspeaker-distribution");

  return (
    <Case1Context.Provider
      value={{
        setActiveSlide,
        activeSlide,
      }}
    >
      <TechnicalDetailsContent title={title} />
    </Case1Context.Provider>
  );
};

const TechnicalDetailsContent: React.FC<TechnicalDetails> = ({ title }) => {
  const ui = useUi();
  const { formatMessage } = useIntl();
  const [signalFlowOpen, setSignalFlowOpen] = useState(false);
  const { activeSlide } = useContext(Case1Context);

  const handleSignalFlowToggle = useCallback(() => setSignalFlowOpen((open) => !open), []);

  const accordion = (
    <div className={styles.accordion}>
      <ui.Scrollbars>
        <AccordionItem
          id={ACCORDION_IDS.loudspeakerDistribution}
          title={formatMessage(messages.accordionItem1Title)}
          unmountChildrenWhenHidden
        >
          <p>{formatMessage(messages.accordionItem1Description)}</p>
        </AccordionItem>
        <AccordionItem
          id={ACCORDION_IDS.signalFlow}
          title={formatMessage(messages.accordionItem2Title)}
          unmountChildrenWhenHidden
        >
          <p>{formatMessage(messages.accordionItem2Description)}</p>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleSignalFlowToggle}>
            {formatMessage(messages.readMore)}
          </ui.Button>
        </AccordionItem>
      </ui.Scrollbars>
    </div>
  );

  let content: React.ReactNode = activeSlide;

  switch (activeSlide) {
    case "loudspeaker-distribution":
      content = <SpeakerDistributionSlide />;
      break;
    case "signal-flow":
      content = (
        <>
          <h3>{formatMessage(messages.SignalFlowImageTitle)}</h3>
          <PhotoView
            key={"logical"}
            imgSrc={`${appConfig.STATIC_ASSETS_URL}/lms/case/case05/technical-details/en/signal-flow.svg`}
            imgMode={"contain"}
          />
        </>
      );
      break;
  }

  return (
    <StandardCase
      title={title}
      leftComponent={
        <Accordion allowMultiOpen={false} defaultOpen={[ACCORDION_IDS.loudspeakerDistribution]}>
          <MainAccordionObserver />
          {accordion}
        </Accordion>
      }
    >
      <div className={styles.contentWrapper}>
        <div className={styles.content}>{content}</div>
      </div>
      <SignalFlowModal visible={signalFlowOpen} onClose={handleSignalFlowToggle} />
    </StandardCase>
  );
};

const MainAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(Case1Context);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: CaseSlide = "signal-flow";
      switch (openItem) {
        case ACCORDION_IDS.loudspeakerDistribution:
          slide = "loudspeaker-distribution";
          break;
        case ACCORDION_IDS.signalFlow:
          slide = "signal-flow";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};
